import { useState, useEffect, useRef } from "react";
import { DialogHeader } from "../../../components/tailwind_componentes/Dialog";
import { CircularProgress, DialogContentText } from "@mui/material";
import moment from "moment-timezone";
import {
  CustomDialog,
  CustomDialogActions,
  CustomDialogContent,
  CustomDialogTitle,
} from "../../../components/customDialog";
import { CloseButton, SendButton } from "../../../components/buttons";
import MessageIcon from "@mui/icons-material/Message";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LocalDiningIcon from '@mui/icons-material/LocalDining';
import chairImage from "../../../assets/images/chair.png";
import { makeRequest, urlWebSockets } from "../../../assets/functions_helper";
import LogoutIcon from '@mui/icons-material/Logout';
import HistoryIcon from '@mui/icons-material/History';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

// --- Funciones Auxiliares (fuera del componente) ---

function generateOrder(order) {
  try {
    if (!order || !order.order_details) return [];

    const orderDetailsObject = typeof order.order_details === 'string' ? JSON.parse(order.order_details) : order.order_details;
    return orderDetailsObject.products.map((product) => ({
      name: product.name,
      quantity: product.quantity,
      price: product.price || 19990,
      comment: product.comment || null,
      image: product.image_url,
      pos_id: product.pos_id,
      is_complex: product.is_complex,
      detail_variants: product.detail_variants,
    }));
  } catch (error) {
    console.error("Error al parsear order_details:", error);
    return [];
  }
}

// Normalizar fechas futuras
const normalizeDate = (timestamp) => {
  if (!timestamp) return moment();

  const parsedDate = moment.utc(timestamp);
  const now = moment();

  // Si la fecha está en el futuro (más de 1 año adelante), ajustarla al año actual
  if (parsedDate.isAfter(now) && parsedDate.diff(now, 'years') >= 1) {
    return parsedDate.year(now.year());
  }

  return parsedDate;
};

// Obtener color de borde según tiempo transcurrido
const getBorderColorByTime = (createdAt) => {
  if (!createdAt) return null;

  const now = moment();
  const created = normalizeDate(createdAt);
  const diffSeconds = now.diff(created, 'seconds');

  if (diffSeconds > 60) return '#FF0000'; // Rojo - más de 1 minuto
  if (diffSeconds > 30) return '#FFCC00'; // Amarillo - más de 30 segundos
  return null;
};

// Formatear tiempo transcurrido
const formatElapsedTime = (seconds) => {
  if (seconds < 60) return `${seconds} seg`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  return `${hours} h ${minutes % 60} min`;
};

// Calcular segundos transcurridos
const calculateElapsedTime = (timestamp) => {
  if (!timestamp) return 0;
  const now = moment();
  const created = normalizeDate(timestamp);
  return now.diff(created, 'seconds');
};

// Formatear timestamp para mostrar
const formatTimestamp = (timestamp) => {
  if (!timestamp) return "Fecha no disponible";

  const normalized = normalizeDate(timestamp);
  return normalized.format("DD-MM-YYYY h:mm A");
};

// Información del tipo de notificación
const getNotificationTypeInfo = (type) => {
  switch (type) {
    case "commented_call":
      return {
        title: "Mensaje de Cliente",
        icon: <MessageIcon className="text-yellow-700" />,
        color: "border-yellow-500",
        barColor: "bg-yellow-500",
        hasDetail: true,
      };
    case "order_call":
      return {
        title: "Nuevo Pedido",
        icon: <ReceiptLongIcon className="text-red-600" />,
        color: "border-red-600",
        barColor: "bg-red-600",
        hasDetail: true,
      };
    case "bill_order":
      return {
        title: "Solicitud de Cuenta",
        icon: <span className="text-purple-600 text-xl">💰</span>,
        color: "border-purple-600",
        barColor: "bg-purple-600",
        hasDetail: true,
      };
    default:
      return {
        title: "Llamada a Mesero",
        icon: <LocalDiningIcon className="text-green-600" />,
        color: "border-green-600",
        barColor: "bg-green-600",
        hasDetail: false,
      };
  }
};

// Procesar y ordenar mesas (prioriza bill_order)
const processAndSortTables = (tablesArray) => {
  return tablesArray
    .map(table => {
      let oldestNotificationTime = null;
      let oldestNotification = null;
      let hasOldNotification = false;
      let highestPriority = 0; // 4: bill, 3: order, 2: comment, 1: normal, 0: none

      if (table.notifications && table.notifications.length > 0) {
        const notificationsWithTime = table.notifications.map(notification => {
          const timestamp = notification.created_at_utc || notification.timestamp;
          const normalizedDate = normalizeDate(timestamp);
          const elapsedSeconds = calculateElapsedTime(timestamp);
          
          // Determinar prioridad según tipo
          let priority = 1; // normal
          if (notification.type === "bill_order") priority = 4;
          else if (notification.type === "order_call") priority = 3;
          else if (notification.type === "commented_call") priority = 2;

          return {
            ...notification,
            momentTime: normalizedDate,
            elapsedSeconds,
            formattedTimestamp: formatTimestamp(timestamp),
            priority
          };
        });

        // Encontrar la notificación más antigua y la de mayor prioridad
        notificationsWithTime.sort((a, b) => a.momentTime - b.momentTime);
        oldestNotification = notificationsWithTime[0];
        oldestNotificationTime = oldestNotification.momentTime;
        hasOldNotification = notificationsWithTime.some(n => n.elapsedSeconds > 10);
        
        // La prioridad de la mesa es la máxima entre sus notificaciones
        highestPriority = Math.max(...notificationsWithTime.map(n => n.priority));
      }

      return {
        ...table,
        oldestNotificationTime,
        oldestNotification,
        borderColor: oldestNotificationTime ? getBorderColorByTime(oldestNotification.momentTime) : null,
        timeDifference: oldestNotificationTime ? calculateElapsedTime(oldestNotification.momentTime) : null,
        hasOldNotification,
        priority: highestPriority,
        notifications: table.notifications ? table.notifications.map(notification => ({
          ...notification,
          elapsedSeconds: calculateElapsedTime(notification.created_at_utc || notification.timestamp),
          formattedTimestamp: formatTimestamp(notification.created_at_utc || notification.timestamp)
        })) : []
      };
    })
    .sort((a, b) => {
      // Primero por prioridad (mayor primero)
      if (a.priority !== b.priority) return b.priority - a.priority;
      // Luego por tiempo de la notificación más antigua
      if (!a.oldestNotificationTime && !b.oldestNotificationTime) return 0;
      if (!a.oldestNotificationTime) return 1;
      if (!b.oldestNotificationTime) return -1;
      return a.oldestNotificationTime - b.oldestNotificationTime;
    });
};

// Calcular estadísticas (incluye bill_orders)
const calculateStatistics = (tables) => {
  let totalCalls = 0;
  let normalCalls = 0;
  let commentCalls = 0;
  let orderCalls = 0;
  let billCalls = 0;
  let tablesWithNotifications = 0;
  let tablesWithVeryOldNotifications = 0;
  let totalWaitingTimeSeconds = 0;
  let tablesWithOrders = 0;
  let tablesWithBillRequests = 0;
  let activeTablesCount = 0;

  const totalTables = tables.length;

  tables.forEach(table => {
    if (table.notifications && table.notifications.length > 0) {
      tablesWithNotifications++;
      activeTablesCount++;
    } else if (table.status === "occupied") {
      activeTablesCount++;
    }

    if (table.notifications && table.notifications.length > 0) {
      table.notifications.forEach(notification => {
        totalCalls++;

        switch (notification.type) {
          case "commented_call":
            commentCalls++;
            if (notification.message && (
              notification.message.toLowerCase().includes("cuenta") ||
              notification.message.toLowerCase().includes("pagar") ||
              notification.message.toLowerCase().includes("factura")
            )) {
              tablesWithBillRequests++;
            }
            break;
          case "order_call":
            orderCalls++;
            tablesWithOrders++;
            break;
          case "bill_order":
            billCalls++;
            tablesWithBillRequests++;
            break;
          default:
            normalCalls++;
            break;
        }

        if (notification.elapsedSeconds) {
          totalWaitingTimeSeconds += notification.elapsedSeconds;
          if (notification.elapsedSeconds > 300) {
            tablesWithVeryOldNotifications++;
          }
        }
      });
    }
  });

  const averageServiceTimeMinutes = totalCalls > 0
    ? Math.round(totalWaitingTimeSeconds / totalCalls / 60)
    : 0;

  return {
    totalTables,
    activeTables: activeTablesCount,
    tablesWithNotifications,
    waitingOrders: tablesWithOrders,
    billRequests: tablesWithBillRequests,
    averageServiceTime: averageServiceTimeMinutes,
    newAlerts: tablesWithVeryOldNotifications,
    totalCalls,
    normalCalls,
    commentCalls,
    orderCalls,
    billCalls
  };
};

// ---------------------------- COMPONENTE PRINCIPAL -------------------------------
export default function CallsV2({ restaurantId }) {
  const [tables, setTables] = useState([]);
  const [selectedTableIndex, setSelectedTableIndex] = useState(null);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTableCompletedInfo, setSelectedTableCompletedInfo] = useState(null);

  const wsRef = useRef(null);
  const [openModalNotification, setOpenModalNotification] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const lastDismissedTimeRef = useRef(null);

  // Estado para el modal de confirmación de cierre de sesión
  const [openCloseSessionConfirm, setOpenCloseSessionConfirm] = useState(false);
  const [tableToClose, setTableToClose] = useState(null);

  const [sortedTables, setSortedTables] = useState([]);
  const [currentTime, setCurrentTime] = useState(moment());

  const [statistics, setStatistics] = useState({
    totalTables: 0,
    activeTables: 0,
    waitingOrders: 0,
    billRequests: 0,
    averageServiceTime: 0,
    newAlerts: 0,
    totalCalls: 0,
    normalCalls: 0,
    commentCalls: 0,
    orderCalls: 0,
    billCalls: 0
  });

  // Nuevo estado para historial de llamadas
  const [callHistory, setCallHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedHistoryItem, setSelectedHistoryItem] = useState(null);

  const formatCurrency = (amount) => {
    if (!amount) return "$0";
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(amount);
  };

  // Efecto para actualizar el tiempo actual cada segundo
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(moment());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Función para cerrar sesión de una mesa (y resolver todas sus notificaciones)
  const handleCloseSession = async (tablePublicId) => {
    try {
      // 1. Obtener las notificaciones de la mesa actual
      const tableIndex = tables.findIndex(t => t.public_id === tablePublicId);
      if (tableIndex === -1) return;

      const notifications = tables[tableIndex].notifications || [];

      // 2. Marcar todas las notificaciones como atendidas (enviar peticiones en paralelo)
      const markPromises = notifications.map(notification =>
        makeRequest(
          "PUT",
          `restaurant_admin/mark_call/${notification.id}`,
          null,
          "application/json",
          localStorage.getItem("token"),
          localStorage.getItem("tokenType"),
          null,
          null,
          () => {},
          () => {}
        ).catch(err => console.error("Error marcando notificación:", err))
      );

      // 3. Cerrar la sesión (también una petición)
      const closeSessionPromise = makeRequest(
        "POST",
        `calls_router/close_session?table_public_id=${tablePublicId}`,
        null,
        "application/json",
        localStorage.getItem("token"),
        localStorage.getItem("tokenType"),
        null,
        null,
        () => {},
        () => {}
      );

      // 4. Esperar a que todas las operaciones terminen
      await Promise.all([...markPromises, closeSessionPromise]);

      // 5. Actualizar el estado local: eliminar todas las notificaciones de la mesa y marcar session_open = false
      setTables(prevTables => {
        const updated = prevTables.map(table =>
          table.public_id === tablePublicId
            ? { ...table, notifications: [], session_open: false }
            : table
        );
        const processed = processAndSortTables(updated);
        setSortedTables(processed);
        setStatistics(calculateStatistics(processed));
        return processed;
      });

      // 6. Cerrar el modal si está abierto
      setOpenModalNotification(false);
      setSelectedNotification(null);
      setShowHistory(false);
      setSelectedHistoryItem(null);
      setCallHistory([]);

    } catch (error) {
      console.error("Error al cerrar sesión y marcar notificaciones:", error);
    }
  };

  // Abrir modal de confirmación para cerrar sesión
  const openCloseSessionConfirmDialog = (tableId) => {
    setTableToClose(tableId);
    setOpenCloseSessionConfirm(true);
  };

  // Función para procesar una llamada del historial al formato esperado
  const processHistoryCall = (call) => {
    const normalizedDate = normalizeDate(call.created_at);
    let orderArray = [];
    
    if (call.call_type === 'order_call' && call.order_details) {
      // order_details puede ser string o objeto
      if (typeof call.order_details === 'string') {
        try {
          const parsed = JSON.parse(call.order_details);
          orderArray = parsed.products || [];
        } catch (e) {
          console.error('Error parsing order_details string:', e);
        }
      } else if (call.order_details.products) {
        orderArray = call.order_details.products;
      }
    }

    return {
      id: call.id,
      type: call.call_type,
      created_at_utc: normalizedDate.toISOString(),
      timestamp: normalizedDate.format("DD-MM-YYYY h:mm A"),
      formattedTimestamp: formatTimestamp(call.created_at),
      message: call.call_type === "commented_call" ? call.comment : undefined,
      order: orderArray,
      elapsedSeconds: calculateElapsedTime(call.created_at),
    };
  };

  // Nueva función para obtener historial de llamadas de una sesión
  const fetchCallHistory = async (sessionId) => {
    setHistoryLoading(true);
    try {
      const response = await makeRequest(
        "GET",
        `restaurants/sessions/${sessionId}/calls`,
        null,
        "application/json",
        localStorage.getItem("token"),
        localStorage.getItem("tokenType"),
        null,
        null,
        () => {},
        () => {}
      );
      const processedCalls = (response.calls || []).map(processHistoryCall);
      setCallHistory(processedCalls);
    } catch (error) {
      console.error("Error fetching call history:", error);
      setCallHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Manejador para mostrar historial
  const handleShowHistory = () => {
    if (selectedTableCompletedInfo?.session_id) {
      fetchCallHistory(selectedTableCompletedInfo.session_id);
      setShowHistory(true);
      setSelectedHistoryItem(null);
      setSelectedNotification(null);
    }
  };

  // Volver a notificaciones desde historial
  const handleBackToNotifications = () => {
    setShowHistory(false);
    setSelectedHistoryItem(null);
    setSelectedNotification(null);
    setCallHistory([]);
  };

  // Ver detalle de una llamada del historial
  const handleHistoryDetail = (call) => {
    setSelectedHistoryItem({ ...call, isHistory: true });
    setSelectedNotification(null);
  };

  // Volver a lista de historial desde detalle
  const handleBackToHistoryList = () => {
    setSelectedHistoryItem(null);
  };

  // Efecto de carga inicial y WebSocket
  useEffect(() => {
    if (!restaurantId) return;

    const fetchData = async (selectedRestaurantId) => {
      const startTime = new Date();
      const endTime = new Date(startTime.getTime() - 60 * 60 * 1000);

      const startTimeStr = startTime.toISOString();
      const endTimeStr = endTime.toISOString();

      const response = await makeRequest(
        "GET",
        `restaurant_admin/calls/${restaurantId}?start_time=${encodeURIComponent(
          endTimeStr
        )}&end_time=${encodeURIComponent(startTimeStr)}`,
        null,
        "application/json",
        localStorage.getItem("token"),
        localStorage.getItem("tokenType"),
        null,
        null,
        () => {
          console.log("Redirect to login");
        }
      );

      const updatedCalls = response.map((call) => ({
        ...call,
        created_at_local: moment
          .utc(call.created_at)
          .tz(moment.tz.guess())
          .format("YYYY-MM-DD HH:mm:ss"),
      }));

      // Nuevo endpoint: restaurants/${restaurantId}/tables
      makeRequest(
        "GET",
        `restaurants/${restaurantId}/tables`,
        null,
        "application/json",
        localStorage.getItem("token"),
        localStorage.getItem("tokenType"),
        null,
        null,
        () => { }
      )
        .then((response) => {
          // Ahora response es un array directamente
          if (Array.isArray(response)) {
            const arrTables = response.map((table) => {
              table.notifications = []; // Inicializar notificaciones vacías
              return table;
            });

            const attendedCalls = updatedCalls.filter(
              (call) => call.attended === false
            );

            let initialNewsNotifications = attendedCalls.map((call) => {
              const normalizedDate = normalizeDate(call.created_at);

              return {
                id: call.call_public_id
                  ? call.call_public_id
                  : Math.random().toString(36).substr(2, 9),
                tableId: call.restaurant_table_public_id
                  ? call.restaurant_table_public_id
                  : call.table_public_id
                    ? call.table_public_id
                    : null,
                type: call.call_type,
                details: `New ${call.call_type
                  .toLowerCase()
                  .replace(/_/g, " ")
                  .replace(/\b\w/g, (char) => char.toUpperCase())}`,
                timestamp: normalizedDate.format("DD-MM-YYYY h:mm A"),
                created_at_utc: normalizedDate.toISOString(),
                message:
                  call.call_type === "commented_call"
                    ? call.comment
                    : undefined,
                order:
                  call.call_type === "order_call"
                    ? generateOrder(call)
                    : undefined,
              };
            });

            const updatedTables = arrTables.map((table) => {
              const tableNotifications = initialNewsNotifications.filter(
                (notification) => notification.tableId === table.public_id
              );

              return {
                ...table,
                notifications: [...table.notifications, ...tableNotifications],
              };
            });

            const processedTables = processAndSortTables(updatedTables);
            setTables(processedTables);
            setSortedTables(processedTables);
            setStatistics(calculateStatistics(processedTables));
          } else {
            // Si no es array, establecer array vacío
            setTables([]);
            setSortedTables([]);
            setStatistics(calculateStatistics([]));
          }
        })
        .catch((error) => {
          console.log(error);
          setTables([]);
          setSortedTables([]);
          setStatistics(calculateStatistics([]));
        })
        .finally(() => {
          setLoading(false);
        });
    };

    if (wsRef.current) {
      wsRef.current.close();
    }

    const newWs = new WebSocket(
      `${urlWebSockets}/table_notification/${restaurantId}`
    );

    newWs.onopen = () => {
      console.log("WebSocket connected");
    };

    newWs.onmessage = (event) => {
      const newCall = JSON.parse(event.data);
      const normalizedDate = normalizeDate(newCall.created_at);

      const newNotification = {
        id: newCall.call_public_id || Math.random().toString(36).substr(2, 9),
        type: newCall.call_type,
        details: `New ${newCall.call_type.toLowerCase().replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase())}`,
        timestamp: normalizedDate.format("DD-MM-YYYY h:mm A"),
        created_at_utc: normalizedDate.toISOString(),
        message: newCall.call_type === "commented_call" ? newCall.comment : undefined,
        order: newCall.call_type === "order_call" ? generateOrder(newCall) : undefined,
      };

      setTables((prevTables) => {
        const updated = prevTables.map((table) =>
          table.public_id === newCall.restaurant_table_public_id
            ? {
                ...table,
                notifications: [...table.notifications, newNotification],
              }
            : table
        );

        const processedTables = processAndSortTables(updated);
        setSortedTables(processedTables);
        setStatistics(calculateStatistics(processedTables));

        return processedTables;
      });
    };

    newWs.onclose = () => {
      console.log("WebSocket disconnected");
    };

    wsRef.current = newWs;

    fetchData(restaurantId);

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [restaurantId]);

  // Efecto para actualizar el tiempo de las notificaciones en tiempo real
  useEffect(() => {
    const interval = setInterval(() => {
      setTables((prevTables) => {
        const processedTables = processAndSortTables(prevTables);
        setSortedTables(processedTables);
        setStatistics(calculateStatistics(processedTables));
        return processedTables;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Modal de advertencia por mesas antiguas
  useEffect(() => {
    const interval = setInterval(() => {
      setTables((prevTables) => {
        const now = Date.now();
        let hasAnyOldNotifications = false;

        const newTables = prevTables.map((table) => {
          const notificationTime = (notification) => moment(notification.timestamp).toDate().getTime();
          const hasOldNotification = table.notifications.some(
            (notification) => {
              return now - notificationTime(notification) > 10000;
            }
          );

          if (hasOldNotification) hasAnyOldNotifications = true;

          return { ...table, hasOldNotification };
        });

        if (hasAnyOldNotifications) {
          const lastDismissed = lastDismissedTimeRef.current;
          const cooldown = 300000;

          if (!lastDismissed || now - lastDismissed > cooldown) {
            const tableNames = newTables
              .filter((table) => table.hasOldNotification)
              .map((table) => table.name)
              .join(", ");

            setModalMessage(
              `⚠️ Las mesas **${tableNames}** tienen órdenes sin atender por más de 10 segundos.`
            );
            setOpenModal(true);
          }
        }

        return newTables;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const handleTableClick = (index, table) => {
    // Solo permitir clic si la mesa tiene sesión abierta (session_open = true)
    if (!table.session_open) return;
    setSelectedTableIndex(index);
    setSelectedTableCompletedInfo(table);
    setOpenModalNotification(true);
  };

  const CloseNotificationButton = (notification) => {
    if (!notification || selectedTableIndex === null) return;

    const callId = notification.id;
    const element = document.getElementById(`notification-${callId}`);

    const finalizeUI = () => {
      setTables((prevTables) => {
        const updated = prevTables.map((table, index) =>
          index === selectedTableIndex
            ? {
                ...table,
                notifications: table.notifications.filter((n) => n.id !== callId),
              }
            : table
        );

        const processedTables = processAndSortTables(updated);
        setStatistics(calculateStatistics(processedTables));

        return processedTables;
      });

      setSelectedNotification(null);

      if (tables[selectedTableIndex]?.notifications.length <= 1) {
        setOpenModalNotification(false);
      }
    };

    if (element) {
      element.classList.add("opacity-0", "h-0", "p-0", "mb-0", "overflow-hidden");
      setTimeout(() => {
        finalizeUI();
      }, 400);
    } else {
      finalizeUI();
    }

    makeRequest(
      "PUT",
      `restaurant_admin/mark_call/${callId}`,
      null,
      "application/json",
      localStorage.getItem("token"),
      localStorage.getItem("tokenType"),
      null,
      null,
      (response) => {
        console.log("Backend sincronizado correctamente");
      },
      (error) => {
        console.error("Error al sincronizar con el backend:", error);
      }
    );
  };

  const TableItem = ({ table, index }) => {
    const notificationCount = table.notifications.length;
    const isAlert = table.hasOldNotification;
    const borderColor = table.borderColor;
    const isSessionOpen = table.session_open === true; // Si no existe, se considera false

    const mainNotificationType = notificationCount > 0
      ? table.notifications[0].type
      : null;

    const pax = table.max_capacity || 4;

    const getTableIcon = () => {
      if (notificationCount === 0) {
        return null;
      }

      switch (mainNotificationType) {
        case 'order_call':
          return (
            <div className="absolute top-12 left-1/2 transform -translate-x-1/2">
              <div className="bg-yellow-500 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg">
                🛒
              </div>
            </div>
          );
        case 'commented_call':
          return (
            <div className="absolute top-12 left-1/2 transform -translate-x-1/2">
              <div className="bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg">
                💬
              </div>
            </div>
          );
        case 'bill_order':
          return (
            <div className="absolute top-12 left-1/2 transform -translate-x-1/2">
              <div className="bg-purple-600 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg">
                💰
              </div>
            </div>
          );
        default:
          return (
            <div className="absolute top-12 left-1/2 transform -translate-x-1/2">
              <div className="bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg">
                📞
              </div>
            </div>
          );
      }
    };

    return (
      <div
        key={table.public_id}
        className={`flex flex-col items-center justify-center cursor-pointer group relative transition-all duration-300 ${
          !isSessionOpen ? 'opacity-50 cursor-not-allowed' : ''
        }`}
        onClick={() => handleTableClick(index, table)}
      >
        {/* Nombre de la mesa */}
        <div className="mb-6 text-lg font-bold text-gray-900 group-hover:text-amber-600 transition-colors">
          {table.name}
        </div>

        {/* Contenedor de mesa */}
        <div className={`relative ${
          isSessionOpen ? 'group-hover:scale-105 transition-transform duration-300 ease-in-out' : ''
        } ${isAlert || borderColor ? "animate-pulse" : ""}`}>
          <svg
            className="w-20 h-20 text-gray-200 transition-colors"
            viewBox="0 0 100 100"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect
              x="10" y="10" width="80" height="80"
              fill={isAlert ? "#ff3b3b" : "currentColor"}
              rx="15" ry="15"
              className="text-gray-300"
              stroke={borderColor || "transparent"}
              strokeWidth={borderColor ? "4" : "0"}
            />
            <rect
              x="15" y="15" width="70" height="70"
              fill="#D4A35A"
              rx="10" ry="10"
            />
          </svg>

          {isSessionOpen && getTableIcon()}

          {/* Sillas */}
          {Array.from({ length: table.max_capacity || 4 }).slice(0, 4).map((_, chairIndex) => {
            const totalChairs = 4;
            const angle = (Math.PI * 2 * chairIndex) / totalChairs;
            const radius = 45;
            const offsetX = Math.cos(angle) * radius;
            const offsetY = Math.sin(angle) * radius;
            const adjustX = -13;
            const adjustY = -13;
            const rotation = (angle * 180) / Math.PI + 90;

            return (
              <img
                key={chairIndex}
                src={chairImage}
                alt="Silla"
                className="absolute w-7 h-7 object-contain transition-transform transform group-hover:scale-110"
                style={{
                  top: `calc(50% + ${offsetY + adjustY}px)`,
                  left: `calc(50% + ${offsetX + adjustX}px)`,
                  transform: `rotate(${rotation}deg)`,
                  filter: borderColor === '#FF0000'
                    ? `drop-shadow(0 0 5px #FF0000)`
                    : borderColor === '#FFCC00'
                      ? `drop-shadow(0 0 5px #FFCC00)`
                      : 'none'
                }}
              />
            );
          })}

          {/* Contador de notificaciones */}
          {isSessionOpen && notificationCount > 0 && (
            <div
              className={`absolute -top-2 -right-2 rounded-full w-7 h-7 flex items-center justify-center text-white text-xs font-bold ring-2 ring-white ${
                borderColor === '#FF0000'
                  ? 'bg-red-700 animate-pulse'
                  : borderColor === '#FFCC00'
                    ? 'bg-yellow-600'
                    : 'bg-red-500'
              }`}
            >
              {notificationCount}
            </div>
          )}
        </div>

        {/* Información adicional debajo de la mesa */}
        <div className="mt-5 text-center">
          {isSessionOpen ? (
            notificationCount > 0 ? (
              <>
                <p className="text-sm font-semibold text-gray-700 mb-1">
                  {mainNotificationType === 'order_call' ? 'ESPERANDO PEDIDO' :
                   mainNotificationType === 'commented_call' ? 'MENSAJE' :
                   mainNotificationType === 'bill_order' ? 'CUENTA SOLICITADA' :
                   'OCUPADA'}
                </p>
                <p className="text-xs text-gray-500">
                  Pax: {pax} • Notif: {notificationCount}
                </p>
              </>
            ) : (
              <p className="text-sm text-gray-500">Disponible</p>
            )
          ) : (
            <p className="text-sm text-gray-400">Sesión cerrada</p>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return null;
  }

  if (!restaurantId) {
    return (
      <div className="text-center p-10 text-xl text-red-500 bg-white">
        Error: No se proporcionó un ID de restaurante.
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-gray-900 p-6">
      {/* Encabezado */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">
          Monitoreo de Mesas de Restaurante
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Monitoreo en tiempo real • {currentTime.format('h:mm:ss A')}
        </p>
      </div>

      {/* Resumen de Llamadas */}
      <div className="mb-8">
        <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
          <span className="mr-2">📊</span> Resumen de Llamadas Activas
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 text-center">
            <p className="text-sm text-gray-500 mb-1">Total Llamadas</p>
            <p className="text-2xl font-bold text-gray-900">{statistics.totalCalls}</p>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 text-center">
            <p className="text-sm text-blue-600 mb-1">Llamadas Normales</p>
            <p className="text-2xl font-bold text-blue-700">{statistics.normalCalls}</p>
          </div>
          <div className="bg-green-50 p-4 rounded-lg border border-green-200 text-center">
            <p className="text-sm text-green-600 mb-1">Con Comentarios</p>
            <p className="text-2xl font-bold text-green-700">{statistics.commentCalls}</p>
          </div>
          <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200 text-center">
            <p className="text-sm text-yellow-600 mb-1">Pedidos</p>
            <p className="text-2xl font-bold text-yellow-700">{statistics.orderCalls}</p>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg border border-purple-200 text-center">
            <p className="text-sm text-purple-600 mb-1">Cuentas</p>
            <p className="text-2xl font-bold text-purple-700">{statistics.billCalls}</p>
          </div>
        </div>
      </div>

      {/* Sección de mesas */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-900">
            Mesas del restaurante
          </h2>
          <div className="text-sm text-gray-600">
            <span className="font-bold">{statistics.tablesWithNotifications}</span> con notificaciones • 
            <span className="font-bold ml-2">{tables.length - statistics.tablesWithNotifications}</span> disponibles
          </div>
        </div>
        <p className="text-sm text-gray-500 mb-8">
          {tables.length} mesas en total • Última actualización: {currentTime.format('h:mm:ss A')}
        </p>
      </div>

      {/* Grid de mesas */}
      <div className="bg-white p-8 rounded-xl border border-gray-200">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-8">
          {sortedTables.length > 0 ? (
            sortedTables.map((table, index) => (
              <TableItem key={table.public_id} table={table} index={index} />
            ))
          ) : (
            <div className="col-span-full text-center py-12">
              <div className="text-gray-400 mb-4">
                <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
              <p className="text-lg text-gray-500 font-medium">
                No hay mesas disponibles o activas para este restaurante.
              </p>
              <p className="text-sm text-gray-400 mt-2">
                Todas las mesas están atendidas correctamente. 🎉
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Modal de Notificaciones de Mesa */}
      <CustomDialog
        open={openModalNotification}
        onClose={() => {
          setOpenModalNotification(false);
          setSelectedNotification(null);
          setShowHistory(false);
          setSelectedHistoryItem(null);
          setCallHistory([]);
        }}
      >
        <CustomDialogContent className="text-gray-900 p-6 bg-white rounded-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader className="border-b border-gray-100 pb-4 mb-4">
            <div className="flex flex-col gap-2">
              {/* Universal Back Button Row */}
              {(showHistory || selectedNotification) && (
                <div className="flex items-center -ml-2 mb-1">
                  {showHistory && selectedHistoryItem ? (
                    <button onClick={handleBackToHistoryList} className="flex items-center text-sm text-gray-500 hover:text-gray-900 font-medium px-2 py-1.5 rounded-md hover:bg-gray-100 transition-colors">
                      <ArrowBackIcon sx={{fontSize:18, mr:0.5}}/> Volver al historial
                    </button>
                  ) : showHistory && !selectedHistoryItem ? (
                    <button onClick={handleBackToNotifications} className="flex items-center text-sm text-gray-500 hover:text-gray-900 font-medium px-2 py-1.5 rounded-md hover:bg-gray-100 transition-colors">
                      <ArrowBackIcon sx={{fontSize:18, mr:0.5}}/> Volver a notificaciones
                    </button>
                  ) : !showHistory && selectedNotification ? (
                    <button onClick={() => setSelectedNotification(null)} className="flex items-center text-sm text-gray-500 hover:text-gray-900 font-medium px-2 py-1.5 rounded-md hover:bg-gray-100 transition-colors">
                      <ArrowBackIcon sx={{fontSize:18, mr:0.5}}/> Volver a notificaciones
                    </button>
                  ) : null}
                </div>
              )}

              {/* Title and Action Row */}
              <CustomDialogTitle className="text-2xl font-bold text-gray-900 flex justify-between items-center w-full !p-0">
                <div className="flex items-center">
                  <span>
                    {showHistory && selectedHistoryItem ? 'Detalle' :
                     showHistory && !selectedHistoryItem ? 'Historial de Llamadas' :
                     !showHistory && selectedNotification ? 'Detalle' :
                     'Notificaciones'}
                  </span>
                  {!showHistory && !selectedNotification && selectedTableCompletedInfo?.session_open && (
                    <button
                      onClick={handleShowHistory}
                      className="ml-4 text-sm bg-gray-200 hover:bg-gray-300 px-3 py-1 rounded-full flex items-center transition-colors"
                    >
                      <HistoryIcon sx={{ fontSize: 18, marginRight: '4px' }} />
                      Historial completo
                    </button>
                  )}
                </div>
                {selectedTableCompletedInfo?.hasOldNotification && !showHistory && !selectedNotification && (
                  <span className="text-sm font-semibold text-red-500 flex items-center bg-red-50 px-3 py-1 rounded-full border border-red-100">
                    <i className="fas fa-exclamation-triangle mr-2"></i>
                    Mesas sin atender
                  </span>
                )}
              </CustomDialogTitle>
              
              <div className="flex justify-between items-center">
                <p className="text-lg text-gray-500 font-medium">
                  Mesa {selectedTableCompletedInfo ? selectedTableCompletedInfo.name : "N/A"}
                </p>
                {selectedTableCompletedInfo?.session_open && !showHistory && !selectedNotification && (
                  <SendButton
                    onClick={() => openCloseSessionConfirmDialog(selectedTableCompletedInfo.public_id)}
                    variant="outline"
                    size="sm"
                    className="text-red-500 hover:text-red-700 border-red-200 hover:border-red-300 bg-red-50 hover:bg-red-100 shadow-none transition-colors"
                  >
                    <LogoutIcon sx={{ fontSize: 18, marginRight: '4px' }} />
                    Cerrar sesión de la mesa
                  </SendButton>
                )}
              </div>
            </div>
          </DialogHeader>

          {/* HISTORY VIEW */}
          {showHistory && !selectedHistoryItem && (
            <div className="space-y-4 mt-6">
              {historyLoading ? (
                <div className="flex justify-center py-8">
                  <CircularProgress />
                </div>
              ) : callHistory.length === 0 ? (
                <p className="text-center py-6 text-gray-500">
                  No hay llamadas previas para esta sesión.
                </p>
              ) : (
                <>
                  {/* Línea de tiempo (Timeline View) */}
                  <div className="relative border-l border-gray-200 ml-4 py-2 space-y-6">
                    {[...callHistory]
                      .sort((a, b) => new Date(b.created_at_utc) - new Date(a.created_at_utc))
                      .map((call, index) => {
                        const typeInfo = getNotificationTypeInfo(call.type);
                        
                        return (
                          <div key={call.id} className="relative pl-6 group">
                            {/* Dot timeline */}
                            <div className={`absolute -left-[5px] top-4 w-2.5 h-2.5 rounded-full ${typeInfo.barColor} ring-4 ring-white shadow-sm transition-transform duration-300 group-hover:scale-125`}></div>
                            
                            {/* Card content */}
                            <div className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 p-4 relative overflow-hidden">
                               <div className="flex justify-between items-center">
                                 <div className="flex items-center gap-4">
                                   <div className={`p-2 rounded-xl bg-gray-50 text-xl border border-gray-100 shadow-inner ${typeInfo.color.replace('border-', 'text-')}`}>
                                      {typeInfo.icon}
                                   </div>
                                   <div>
                                     <h4 className="font-extrabold text-gray-900 text-[15px]">
                                       {call.type === 'order_call'
                                        ? `Pedido de ${call.order?.length || 0} ${call.order?.length === 1 ? 'producto' : 'productos'}`
                                        : call.type === 'commented_call'
                                        ? 'Mensaje del cliente'
                                        : typeInfo.title}
                                     </h4>
                                     <p className="text-[11px] font-semibold text-gray-400 mt-0.5">{call.formattedTimestamp}</p>
                                   </div>
                                 </div>
                                 <SendButton
                                   variant="secondary"
                                   size="sm"
                                   onClick={() => handleHistoryDetail(call)}
                                   className="opacity-0 group-hover:opacity-100 transition-opacity bg-gray-50 text-gray-700 hover:bg-gray-100 shadow-sm"
                                 >
                                   Ver detalle
                                 </SendButton>
                               </div>

                               {/* Previsualización para mensajes de cliente */}
                               {call.type === 'commented_call' && call.message && (
                                  <div className="mt-3 ml-[60px] bg-amber-50/60 p-3 rounded-lg text-sm text-gray-600 italic border border-amber-100/50">
                                     "{call.message}"
                                  </div>
                               )}
                               
                               {/* Previsualización para pedidos rápidos  */}
                               {call.type === 'order_call' && call.order && call.order.length > 0 && (
                                  <div className="mt-3 ml-[60px] flex gap-2 flex-wrap pb-1">
                                     {call.order.slice(0, 3).map((prod, i) => (
                                       <span key={i} className="inline-flex items-center px-2 py-1 rounded-md text-[11px] font-bold bg-gray-100 text-gray-600 border border-gray-200 shadow-sm">
                                          {prod.quantity}x {prod.name}
                                       </span>
                                     ))}
                                     {call.order.length > 3 && (
                                       <span className="inline-flex items-center px-2 py-1 rounded-md text-[11px] font-bold bg-gray-50 text-gray-400">
                                          +{call.order.length - 3} más
                                       </span>
                                     )}
                                  </div>
                               )}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </>
              )}
            </div>
          )}

          {/* HISTORY DETAIL VIEW */}
          {showHistory && selectedHistoryItem && (
            <div className="space-y-4 pt-2">

              {/* DETALLE: BILL ORDER */}
              {selectedHistoryItem.type === "bill_order" && (
                <div className="p-5 bg-purple-50 rounded-lg border border-purple-200">
                  <div className="flex items-center mb-3">
                    <div className="w-1 h-6 rounded-full mr-3 flex-shrink-0 bg-purple-600"></div>
                    <h4 className="font-semibold text-gray-900">El cliente solicita la cuenta</h4>
                  </div>
                  <p className="text-lg italic text-gray-700 ml-4">
                    El cliente ha pedido la cuenta.
                  </p>
                  <p className="text-xs text-gray-500 mt-2 text-right">
                    {selectedHistoryItem.formattedTimestamp}
                  </p>
                </div>
              )}

              {/* DETALLE: COMMENTED CALL */}
              {selectedHistoryItem.type === "commented_call" && (
                <div className="p-5 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center mb-3">
                    <div className="w-1 h-6 rounded-full mr-3 flex-shrink-0 bg-yellow-500"></div>
                    <h4 className="font-semibold text-gray-900">Mensaje del cliente:</h4>
                  </div>
                  <p className="text-lg italic text-gray-700 ml-4">
                    "{selectedHistoryItem.message}"
                  </p>
                  <p className="text-xs text-gray-500 mt-2 text-right">
                    {selectedHistoryItem.formattedTimestamp}
                  </p>
                </div>
              )}

              {/* DETALLE: ORDER CALL */}
              {selectedHistoryItem.type === "order_call" && (
                <div className="space-y-6">
                  {selectedHistoryItem.order?.map((item, index) => {
                    const extrasTotalPerUnit = item.is_complex && item.detail_variants
                      ? item.detail_variants.reduce((sum, group) => sum + group.details.reduce((gSum, opt) => gSum + (opt.value || 0) * (opt.quantity || 1), 0), 0)
                      : 0;
                    const unitTotal = item.price + extrasTotalPerUnit;
                    const itemSubtotal = unitTotal * item.quantity;
                    
                    return (
                    <div
                      key={index}
                      className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden flex flex-col sm:flex-row"
                    >
                      <div className="sm:w-40 sm:h-40 w-full h-48 flex-shrink-0 bg-gray-50 flex items-center justify-center overflow-hidden">
                        {item.image_url ? (
                          <img
                            src={item.image_url}
                            alt={item.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = "https://via.placeholder.com/150?text=Sin+Imagen";
                            }}
                          />
                        ) : (
                          <div className="flex flex-col items-center justify-center text-gray-400">
                            <svg className="w-8 h-8 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <span className="text-xs font-medium">Sin imagen</span>
                          </div>
                        )}
                      </div>

                      <div className="flex-grow p-6 flex flex-col justify-between">
                        <div>
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-3">
                              <div className="w-1.5 h-8 rounded-full bg-red-500"></div>
                              <div>
                                <h4 className="font-extrabold text-xl text-gray-800">{item.name}</h4>
                                <p className="text-xs text-gray-500 mt-1">ID: {item.pos_id || 'N/A'}</p>
                              </div>
                            </div>
                            <div className="bg-gray-100 px-3 py-1 rounded-full">
                              <span className="text-sm font-bold text-gray-600">
                                cant. {item.quantity}
                              </span>
                            </div>
                          </div>

                          {item.comment && (
                            <div className="bg-amber-50 p-3 rounded-lg mb-4 text-sm text-amber-800 border border-amber-100">
                              <p className="italic">"{item.comment}"</p>
                            </div>
                          )}

                          {item.is_complex && item.detail_variants && item.detail_variants.length > 0 && (
                            <div className="bg-gray-50 rounded-lg p-3 mb-4 text-sm border border-gray-100 shadow-sm">
                              <h5 className="font-bold text-gray-700 mb-2 border-b border-gray-200 pb-1 flex items-center">
                                <span className="mr-2">📋</span> Variantes seleccionadas
                              </h5>
                              {item.detail_variants.map((group, gIdx) => (
                                <div key={gIdx} className="mb-2 last:mb-0">
                                  <p className="font-semibold text-gray-600 text-xs uppercase tracking-wider">{group.option_display_name}</p>
                                  <ul className="mt-1 space-y-1">
                                    {group.details.map((opt, oIdx) => (
                                      <li key={oIdx} className="flex justify-between items-center bg-white px-2 py-1.5 rounded-md border border-gray-100 shadow-sm">
                                        <div className="flex items-center gap-2">
                                          <span className="bg-gray-200 text-gray-700 font-bold px-1.5 py-0.5 rounded text-xs">{opt.quantity}x</span>
                                          <span className="text-gray-800 font-medium text-xs">{opt.name}</span>
                                        </div>
                                        {opt.value > 0 && (
                                          <span className="text-gray-500 font-semibold text-xs ml-4">
                                            +{formatCurrency(opt.value * opt.quantity)}
                                          </span>
                                        )}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        <div className="grid grid-cols-3 gap-2 pt-4 border-t border-gray-100">
                          <div className="flex flex-col">
                            <span className="text-xs text-gray-500">Unitario</span>
                            <span className="font-semibold text-gray-700">{formatCurrency(unitTotal)}</span>
                          </div>
                          <div className="flex flex-col items-center">
                            <span className="text-xs text-gray-500">Cantidad</span>
                            <span className="font-bold text-gray-800">{item.quantity}</span>
                          </div>
                          <div className="flex flex-col items-end">
                            <span className="text-xs text-red-500">Subtotal</span>
                            <span className="font-black text-lg text-red-600">{formatCurrency(itemSubtotal)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )})}

                  {selectedHistoryItem.order && selectedHistoryItem.order.length > 1 && (
                    <div className="mt-6 p-4 bg-gray-900 rounded-xl">
                      <div className="flex justify-between items-center text-white">
                        <span className="text-lg font-medium">Total del pedido</span>
                        <p className="text-2xl font-black">
                          {formatCurrency(
                            selectedHistoryItem.order.reduce(
                              (sum, item) => {
                                const extras = item.is_complex && item.detail_variants ? item.detail_variants.reduce((s, g) => s + g.details.reduce((gs, o) => gs + (o.value || 0)*(o.quantity || 1), 0), 0) : 0;
                                return sum + (item.price + extras) * item.quantity;
                              },
                              0
                            )
                          )}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* NOTIFICATIONS LIST VIEW */}
          {!showHistory && selectedTableIndex !== null && !selectedNotification && (
            tables[selectedTableIndex]?.notifications.length > 0 ? (
              <div className="space-y-4 mt-6">
                {/* Notificaciones agrupadas (primero bill_order, luego order, luego comment, luego normal) */}
                {tables[selectedTableIndex].notifications.filter(n => n.type === 'bill_order').length > 0 && (
                  <h3 className="text-lg font-bold mt-4 text-purple-700">Solicitud de Cuenta</h3>
                )}
                {tables[selectedTableIndex].notifications
                  .filter(n => n.type === 'bill_order')
                  .map((notification) => {
                    const { title, barColor, hasDetail } = getNotificationTypeInfo(notification.type);
                    return (
                      <div
                        key={notification.id}
                        className={`flex flex-col p-4 bg-gray-50 rounded-lg border border-gray-200 transition-all duration-500 ease-in-out`}
                        id={`notification-${notification.id}`}
                      >
                        <div className="flex justify-between items-center space-x-3">
                          <div className={`w-1 h-12 rounded-full mr-3 flex-shrink-0 ${barColor}`}></div>
                          <div className="flex-grow">
                            <div className="flex justify-between items-start">
                              <p className="font-semibold text-gray-900">{title}</p>
                              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                                notification.elapsedSeconds > 60 ? 'bg-red-100 text-red-700' :
                                notification.elapsedSeconds > 30 ? 'bg-yellow-100 text-yellow-700' :
                                'bg-green-100 text-green-700'
                              }`}>
                                {formatElapsedTime(notification.elapsedSeconds)}
                              </span>
                            </div>
                            <p className="text-sm text-gray-500 mt-1">{notification.formattedTimestamp || notification.timestamp}</p>
                          </div>
                          <div className="space-x-2 flex-shrink-0">
                            {/* Para bill_order: botones "Descartar" y "Cerrar sesión" */}
                            {notification.type === 'bill_order' ? (
                              <>
                                <CloseButton
                                  variant="destructive"
                                  size="sm"
                                  onClick={() => CloseNotificationButton(notification)}
                                >
                                  Descartar
                                </CloseButton>
                                <SendButton
                                  variant="secondary"
                                  size="sm"
                                  onClick={() => openCloseSessionConfirmDialog(selectedTableCompletedInfo.public_id)}
                                >
                                  <LogoutIcon sx={{ fontSize: 16, marginRight: '4px' }} />
                                  Cerrar sesión
                                </SendButton>
                              </>
                            ) : (
                              <>
                                {hasDetail && (
                                  <SendButton
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => setSelectedNotification(notification)}
                                    className="bg-blue-50 text-blue-600 hover:bg-blue-100 hover:text-blue-700 border border-blue-200 shadow-sm"
                                  >
                                    Ver detalle
                                  </SendButton>
                                )}
                                <CloseButton
                                  variant="destructive"
                                  size="sm"
                                  onClick={() => CloseNotificationButton(notification)}
                                >
                                  Resolver
                                </CloseButton>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}

                {tables[selectedTableIndex].notifications.filter(n => n.type === 'order_call').length > 0 && (
                  <h3 className="text-lg font-bold mt-6 text-gray-700">Nuevo Pedido</h3>
                )}
                {tables[selectedTableIndex].notifications
                  .filter(n => n.type === 'order_call')
                  .map((notification) => {
                    const { barColor } = getNotificationTypeInfo(notification.type);
                    const itemCount = notification.order?.length || 0;
                    return (
                      <div
                        key={notification.id}
                        className={`flex flex-col p-4 bg-gray-50 rounded-lg border border-gray-200 transition-all duration-500 ease-in-out`}
                        id={`notification-${notification.id}`}
                      >
                        <div className="flex justify-between items-center space-x-3">
                          <div className={`w-1 h-12 rounded-full mr-3 flex-shrink-0 ${barColor}`}></div>
                          <div className="flex-grow">
                            <div className="flex justify-between items-start">
                              <p className="font-semibold text-gray-900">
                                {itemCount > 0 
                                  ? `Pedido de ${itemCount} ${itemCount === 1 ? 'producto' : 'productos'}` 
                                  : 'Pedido vacío'}
                              </p>
                              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                                notification.elapsedSeconds > 60 ? 'bg-red-100 text-red-700' :
                                notification.elapsedSeconds > 30 ? 'bg-yellow-100 text-yellow-700' :
                                'bg-green-100 text-green-700'
                              }`}>
                                {formatElapsedTime(notification.elapsedSeconds)}
                              </span>
                            </div>
                            <p className="text-sm text-gray-500 mt-1">{notification.formattedTimestamp || notification.timestamp}</p>
                          </div>
                          <div className="space-x-2 flex-shrink-0">
                            <SendButton
                              variant="secondary"
                              size="sm"
                              onClick={() => setSelectedNotification(notification)}
                              className="bg-blue-50 text-blue-600 hover:bg-blue-100 hover:text-blue-700 border border-blue-200 shadow-sm"
                            >
                              Ver detalle
                            </SendButton>
                            <CloseButton
                              variant="destructive"
                              size="sm"
                              onClick={() => CloseNotificationButton(notification)}
                            >
                              Resolver
                            </CloseButton>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                {tables[selectedTableIndex].notifications.filter(n => n.type === 'commented_call').length > 0 && (
                  <h3 className="text-lg font-bold mt-6 text-gray-700">Mensajes de Cliente</h3>
                )}
                {tables[selectedTableIndex].notifications
                  .filter(n => n.type === 'commented_call')
                  .map((notification) => {
                    const { title, barColor, hasDetail } = getNotificationTypeInfo(notification.type);
                    return (
                      <div
                        key={notification.id}
                        className={`flex flex-col p-4 bg-gray-50 rounded-lg border border-gray-200 transition-all duration-500 ease-in-out`}
                        id={`notification-${notification.id}`}
                      >
                        <div className="flex justify-between items-center space-x-3">
                          <div className={`w-1 h-12 rounded-full mr-3 flex-shrink-0 ${barColor}`}></div>
                          <div className="flex-grow">
                            <div className="flex justify-between items-start">
                              <p className="font-semibold text-gray-900">{notification.message?.substring(0, 20) || title}</p>
                              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                                notification.elapsedSeconds > 60 ? 'bg-red-100 text-red-700' :
                                notification.elapsedSeconds > 30 ? 'bg-yellow-100 text-yellow-700' :
                                'bg-green-100 text-green-700'
                              }`}>
                                {formatElapsedTime(notification.elapsedSeconds)}
                              </span>
                            </div>
                            <p className="text-sm text-gray-500 mt-1">{notification.formattedTimestamp || notification.timestamp}</p>
                          </div>
                          <div className="space-x-2 flex-shrink-0">
                            {hasDetail && (
                              <SendButton
                                variant="secondary"
                                size="sm"
                                onClick={() => setSelectedNotification(notification)}
                              >
                                Ver detalle
                              </SendButton>
                            )}
                            <CloseButton
                              variant="destructive"
                              size="sm"
                              onClick={() => CloseNotificationButton(notification)}
                            >
                              Resolver
                            </CloseButton>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                {/* NUEVA SECCIÓN PARA NORMAL_CALL */}
                {tables[selectedTableIndex].notifications.filter(n => n.type === 'normal_call').length > 0 && (
                  <h3 className="text-lg font-bold mt-6 text-gray-700">Llamadas a Mesero</h3>
                )}
                {tables[selectedTableIndex].notifications
                  .filter(n => n.type === 'normal_call')
                  .map((notification) => {
                    const { title, barColor, hasDetail } = getNotificationTypeInfo(notification.type);
                    return (
                      <div
                        key={notification.id}
                        className={`flex flex-col p-4 bg-gray-50 rounded-lg border border-gray-200 transition-all duration-500 ease-in-out`}
                        id={`notification-${notification.id}`}
                      >
                        <div className="flex justify-between items-center space-x-3">
                          <div className={`w-1 h-12 rounded-full mr-3 flex-shrink-0 ${barColor}`}></div>
                          <div className="flex-grow">
                            <div className="flex justify-between items-start">
                              <p className="font-semibold text-gray-900">{title}</p>
                              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                                notification.elapsedSeconds > 60 ? 'bg-red-100 text-red-700' :
                                notification.elapsedSeconds > 30 ? 'bg-yellow-100 text-yellow-700' :
                                'bg-green-100 text-green-700'
                              }`}>
                                {formatElapsedTime(notification.elapsedSeconds)}
                              </span>
                            </div>
                            <p className="text-sm text-gray-500 mt-1">{notification.formattedTimestamp || notification.timestamp}</p>
                          </div>
                          <div className="space-x-2 flex-shrink-0">
                            {hasDetail && (
                              <SendButton
                                variant="secondary"
                                size="sm"
                                onClick={() => setSelectedNotification(notification)}
                              >
                                Ver detalle
                              </SendButton>
                            )}
                            <CloseButton
                              variant="destructive"
                              size="sm"
                              onClick={() => CloseNotificationButton(notification)}
                            >
                              Resolver
                            </CloseButton>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            ) : (
              <p className="text-center py-6 text-gray-500">
                No hay notificaciones activas para esta mesa. 🎉
              </p>
            )
          )}

          {/* NOTIFICATION DETAIL VIEW */}
          {!showHistory && selectedNotification && (
            <div className="space-y-4 pt-4">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold text-gray-900">
                  {selectedNotification.type === 'order_call' ? 'Nuevo Pedido' :
                   selectedNotification.type === 'bill_order' ? 'Solicitud de Cuenta' :
                   'Detalle de Llamada'}
                </h3>
                {selectedNotification.elapsedSeconds && (
                  <span className={`text-sm font-semibold px-3 py-1 rounded-full ${
                    selectedNotification.elapsedSeconds > 60 ? 'bg-red-100 text-red-700 border border-red-200' :
                    selectedNotification.elapsedSeconds > 30 ? 'bg-yellow-100 text-yellow-700 border border-yellow-200' :
                    'bg-green-100 text-green-700 border border-green-200'
                  }`}>
                    {formatElapsedTime(selectedNotification.elapsedSeconds)}
                  </span>
                )}
              </div>

              {/* DETALLE: BILL ORDER */}
              {selectedNotification.type === "bill_order" && (
                <div className="p-5 bg-purple-50 rounded-lg border border-purple-200">
                  <div className="flex items-center mb-3">
                    <div className={`w-1 h-6 rounded-full mr-3 flex-shrink-0 bg-purple-600`}></div>
                    <h4 className="font-semibold text-gray-900">El cliente solicita la cuenta</h4>
                  </div>
                  <p className="text-lg italic text-gray-700 ml-4">
                    El cliente ha pedido la cuenta. Por favor, acerquese a la mesa.
                  </p>
                  <p className="text-xs text-gray-500 mt-2 text-right">
                    {selectedNotification.formattedTimestamp || selectedNotification.timestamp}
                  </p>
                </div>
              )}

              {/* DETALLE: COMMENTED CALL */}
              {selectedNotification.type === "commented_call" && (
                <div className="p-5 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center mb-3">
                    <div className={`w-1 h-6 rounded-full mr-3 flex-shrink-0 bg-yellow-500`}></div>
                    <h4 className="font-semibold text-gray-900">Mensaje del cliente:</h4>
                  </div>
                  <p className="text-lg italic text-gray-700 ml-4">
                    "{selectedNotification.message}"
                  </p>
                  <p className="text-xs text-gray-500 mt-2 text-right">
                    {selectedNotification.formattedTimestamp || selectedNotification.timestamp}
                  </p>
                </div>
              )}

              {/* DETALLE: ORDER CALL */}
              {selectedNotification.type === "order_call" && (
                <div className="space-y-6">
                  {selectedNotification.order?.map((item, index) => {
                    const extrasTotalPerUnit = item.is_complex && item.detail_variants
                      ? item.detail_variants.reduce((sum, group) => sum + group.details.reduce((gSum, opt) => gSum + (opt.value || 0) * (opt.quantity || 1), 0), 0)
                      : 0;
                    const unitTotal = item.price + extrasTotalPerUnit;
                    const itemSubtotal = unitTotal * item.quantity;
                    
                    return (
                    <div
                      key={index}
                      className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden flex flex-col sm:flex-row"
                    >
                      <div className="sm:w-40 sm:h-40 w-full h-48 flex-shrink-0 bg-gray-50 flex items-center justify-center overflow-hidden">
                        {item.image ? (
                          <img
                            src={item.image}
                            alt={item.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = "https://via.placeholder.com/150?text=Sin+Imagen";
                            }}
                          />
                        ) : (
                          <div className="flex flex-col items-center justify-center text-gray-400">
                            <svg className="w-8 h-8 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <span className="text-xs font-medium">Sin imagen</span>
                          </div>
                        )}
                      </div>

                      <div className="flex-grow p-6 flex flex-col justify-between">
                        <div>
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-3">
                              <div className="w-1.5 h-8 rounded-full bg-red-500"></div>
                              <div>
                                <h4 className="font-extrabold text-xl text-gray-800">{item.name}</h4>
                                <p className="text-xs text-gray-500 mt-1">ID: {item.pos_id || 'N/A'}</p>
                              </div>
                            </div>
                            <div className="bg-gray-100 px-3 py-1 rounded-full">
                              <span className="text-sm font-bold text-gray-600">
                                cant. {item.quantity}
                              </span>
                            </div>
                          </div>

                          {item.comment && (
                            <div className="bg-amber-50 p-3 rounded-lg mb-4 text-sm text-amber-800 border border-amber-100">
                              <p className="italic">"{item.comment}"</p>
                            </div>
                          )}

                          {item.is_complex && item.detail_variants && item.detail_variants.length > 0 && (
                            <div className="bg-gray-50 rounded-lg p-3 mb-4 text-sm border border-gray-100 shadow-sm">
                              <h5 className="font-bold text-gray-700 mb-2 border-b border-gray-200 pb-1 flex items-center">
                                <span className="mr-2">📋</span> Variantes seleccionadas
                              </h5>
                              {item.detail_variants.map((group, gIdx) => (
                                <div key={gIdx} className="mb-2 last:mb-0">
                                  <p className="font-semibold text-gray-600 text-xs uppercase tracking-wider">{group.option_display_name}</p>
                                  <ul className="mt-1 space-y-1">
                                    {group.details.map((opt, oIdx) => (
                                      <li key={oIdx} className="flex justify-between items-center bg-white px-2 py-1.5 rounded-md border border-gray-100 shadow-sm">
                                        <div className="flex items-center gap-2">
                                          <span className="bg-gray-200 text-gray-700 font-bold px-1.5 py-0.5 rounded text-xs">{opt.quantity}x</span>
                                          <span className="text-gray-800 font-medium text-xs">{opt.name}</span>
                                        </div>
                                        {opt.value > 0 && (
                                          <span className="text-gray-500 font-semibold text-xs ml-4">
                                            +{formatCurrency(opt.value * opt.quantity)}
                                          </span>
                                        )}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        <div className="grid grid-cols-3 gap-2 pt-4 border-t border-gray-100">
                          <div className="flex flex-col">
                            <span className="text-xs text-gray-500">Unitario</span>
                            <span className="font-semibold text-gray-700">{formatCurrency(unitTotal)}</span>
                          </div>
                          <div className="flex flex-col items-center">
                            <span className="text-xs text-gray-500">Cantidad</span>
                            <span className="font-bold text-gray-800">{item.quantity}</span>
                          </div>
                          <div className="flex flex-col items-end">
                            <span className="text-xs text-red-500">Subtotal</span>
                            <span className="font-black text-lg text-red-600">{formatCurrency(itemSubtotal)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )})}

                  {selectedNotification.order && selectedNotification.order.length > 1 && (
                    <div className="mt-6 p-4 bg-gray-900 rounded-xl">
                      <div className="flex justify-between items-center text-white">
                        <span className="text-lg font-medium">Total del pedido</span>
                        <p className="text-2xl font-black">
                          {formatCurrency(
                            selectedNotification.order.reduce(
                              (sum, item) => {
                                const extras = item.is_complex && item.detail_variants ? item.detail_variants.reduce((s, g) => s + g.details.reduce((gs, o) => gs + (o.value || 0)*(o.quantity || 1), 0), 0) : 0;
                                return sum + (item.price + extras) * item.quantity;
                              },
                              0
                            )
                          )}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end pt-4 mt-6 border-t border-gray-200">
                <CloseButton
                  variant="destructive"
                  size="sm"
                  className="w-full sm:w-auto"
                  onClick={() => CloseNotificationButton(selectedNotification)}
                >
                  <CheckCircleIcon sx={{ fontSize: 18 }} />
                  {selectedNotification.type === 'bill_order' ? 'Descartar' : 'Resolver Notificación'}
                </CloseButton>
              </div>
            </div>
          )}
        </CustomDialogContent>
      </CustomDialog>

      {/* Modal de confirmación para cerrar sesión */}
      <CustomDialog
        open={openCloseSessionConfirm}
        onClose={() => setOpenCloseSessionConfirm(false)}
        maxWidth="sm"
      >
        <CustomDialogTitle className="text-red-500 text-xl font-bold bg-white p-4 border-b border-gray-300">
          ⚠️ Confirmar cierre de sesión
        </CustomDialogTitle>
        <CustomDialogContent className="bg-white text-gray-500 p-6">
          <DialogContentText className="text-lg leading-relaxed text-gray-500">
            ¿Estás seguro de que deseas cerrar la sesión de esta mesa?
            <br />
            Se marcarán todas las notificaciones como atendidas y la mesa quedará libre.
          </DialogContentText>
        </CustomDialogContent>
        <CustomDialogActions className="bg-white p-4 border-t border-gray-300">
          <SendButton
            onClick={() => setOpenCloseSessionConfirm(false)}
            variant="outline"
            color="secondary"
          >
            Cancelar
          </SendButton>
          <CloseButton
            onClick={() => {
              if (tableToClose) {
                handleCloseSession(tableToClose);
                setOpenCloseSessionConfirm(false);
                setTableToClose(null);
              }
            }}
            autoFocus
          >
            Sí, cerrar sesión
          </CloseButton>
        </CustomDialogActions>
      </CustomDialog>

      {/* Modal de Advertencia de Antigüedad */}
      <CustomDialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        maxWidth="sm"
      >
        <CustomDialogTitle className="text-red-500 text-xl font-bold bg-white p-4 border-b border-gray-300">
          🚨 Atención Urgente Requerida
        </CustomDialogTitle>
        <CustomDialogContent className="bg-white text-gray-500 p-6">
          <DialogContentText className="text-lg leading-relaxed text-gray-500">
            <p className="mt-4 text-sm text-gray-500 italic">
              {modalMessage}
            </p>
          </DialogContentText>
          <p className="mt-4 text-sm text-gray-500 italic">
            Por favor, atiende estas llamadas lo antes posible.
          </p>
        </CustomDialogContent>
        <CustomDialogActions className="bg-white p-4 border-t border-gray-300">
          <SendButton
            onClick={() => {
              setOpenModal(false);
              lastDismissedTimeRef.current = Date.now();
            }}
            color="primary"
            autoFocus
          >
            OK, Entendido
          </SendButton>
        </CustomDialogActions>
      </CustomDialog>
    </div>
  );
}